// ./src/components/features/ContentBlocks/ContentBlockPicker/ContentBlockPicker.tsx
import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Text,
  Group,
  Stack,
  ActionIcon,
  Badge,
  Modal,
  Button,
  useMantineTheme,
  useMantineColorScheme,
  Paper,
  Tooltip,
  ScrollArea,
  Select,
  Grid,
  Divider,
  NumberInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { ContentBlock, DisplayContentAssignmentInput } from "@/utils/api";

import { useContentBlocks } from "@/hooks/useContentBlocks";
import { LoadingIndicator, AppIcon } from "@/components/generic";

dayjs.extend(utc);

export interface ContentBlockPickerProps {
  /** Display ID to filter content blocks by (optional for new displays) */
  displayId?: string | null | undefined;

  /** Current content block assignments */
  value: DisplayContentAssignmentInput[] | null | undefined;

  /** Callback when content block assignments change */
  onChange?: (assignments: DisplayContentAssignmentInput[]) => void;

  /** Venue ID to filter content blocks by (optional) */
  venueId?: string | null | undefined;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive content blocks */
  includeInactive?: boolean;

  /** Custom filter function for content blocks */
  contentBlockFilter?: (contentBlock: ContentBlock) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Maximum height for the selected items area */
  maxHeight?: number;
}

interface ContentBlockAssignmentForm {
  contentBlockId: string;
  startDatetime?: string | null;
  endDatetime?: string | null;
  durationSeconds?: number | null;
  orderNo?: number | null;
}

export const ContentBlockPicker: React.FC<ContentBlockPickerProps> = ({
  displayId,
  value,
  onChange,
  venueId,
  placeholder = "No content block assignments",
  includeInactive = false,
  contentBlockFilter,
  loadingPlaceholder = "Loading content blocks...",
  emptyText = "No content blocks available to assign",
  error,
  disabled,
  size = "sm",
  maxHeight = 400,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [assignmentModalOpened, setAssignmentModalOpened] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Use content blocks hook
  const { contentBlocks, loading, lastError } = useContentBlocks({
    venueId,
    autoLoad: true,
    pageSize: 100, // Load more items for selection
  });

  // Form for adding/editing assignments
  const assignmentForm = useForm<ContentBlockAssignmentForm>({
    initialValues: {
      contentBlockId: "",
      startDatetime: null,
      endDatetime: null,
      durationSeconds: null,
      orderNo: undefined,
    },
    validate: {
      contentBlockId: (value) => (!value ? "Content block is required" : null),
      endDatetime: (value, values) => {
        if (value && values.startDatetime && value <= values.startDatetime) {
          return "End date must be after start date";
        }
        return null;
      },
      durationSeconds: (value) => {
        if (value !== null && value !== undefined && value < 1) {
          return "Duration must be at least 1 second";
        }
        return null;
      },
    },
  });

  // Current assignments
  const currentAssignments = useMemo(() => {
    return value || [];
  }, [value]);

  // Filter and prepare content blocks
  const availableContentBlocks = useMemo(() => {
    let filtered = contentBlocks;

    // Apply status filter
    if (!includeInactive) {
      filtered = filtered.filter((block) => block.status === 100);
    }

    // Apply custom filter if provided
    if (contentBlockFilter) {
      filtered = filtered.filter(contentBlockFilter);
    }

    return filtered;
  }, [contentBlocks, includeInactive, contentBlockFilter]);

  // Get content block by ID for display
  const getContentBlockById = useCallback(
    (id: string) => {
      return availableContentBlocks.find((block) => block.id === id);
    },
    [availableContentBlocks]
  );

  // Get content type info for display
  const getContentTypeInfo = (contentType?: string) => {
    switch (contentType) {
      case "TEXT":
        return { icon: "Type", color: "blue" };
      case "IMAGE":
        return { icon: "Image", color: "green" };
      case "VIDEO":
        return { icon: "Video", color: "purple" };
      case "HTML":
        return { icon: "Code", color: "orange" };
      case "MARKDOWN":
        return { icon: "FileText", color: "teal" };
      default:
        return { icon: "File", color: "gray" };
    }
  };

  // Format date for display
  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return "Not set";
    try {
      return dayjs(dateString).format("DD MMM, YYYY @ hh:mm A");
    } catch {
      return "Invalid date";
    }
  };

  // Format duration for display
  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return "Not set";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0)
      parts.push(`${remainingSeconds}s`);

    return parts.join(" ");
  };

  // Handle opening add modal
  const handleOpenAddModal = useCallback(() => {
    assignmentForm.reset();
    // Set default order number
    const maxOrder = Math.max(
      0,
      ...currentAssignments.map((a) => a.orderNo || 0)
    );
    assignmentForm.setFieldValue("orderNo", maxOrder + 1);
    setEditingIndex(null);
    setSearchQuery("");
    setAssignmentModalOpened(true);
  }, [currentAssignments, assignmentForm]);

  // Handle opening edit modal
  const handleOpenEditModal = useCallback(
    (index: number) => {
      const assignment = currentAssignments[index];
      if (assignment) {
        assignmentForm.setValues({
          contentBlockId: assignment.contentBlockId || "",
          startDatetime: assignment.startDatetime
            ? dayjs(assignment.startDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          endDatetime: assignment.endDatetime
            ? dayjs(assignment.endDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          durationSeconds: assignment.durationSeconds,
          orderNo: assignment.orderNo,
        });
        setEditingIndex(index);
        setSearchQuery("");
        setAssignmentModalOpened(true);
      }
    },
    [currentAssignments, assignmentForm]
  );

  // Handle saving assignment
  const handleSaveAssignment = useCallback(() => {
    if (!assignmentForm.isValid()) {
      assignmentForm.validate();
      return;
    }

    const formValues = assignmentForm.values;
    const newAssignment: DisplayContentAssignmentInput = {
      displayId,
      contentBlockId: formValues.contentBlockId,
      startDatetime: formValues.startDatetime
        ? dayjs(formValues.startDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
        : undefined,
      endDatetime: formValues.endDatetime
        ? dayjs(formValues.endDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
        : undefined,
      durationSeconds: formValues.durationSeconds || undefined,
      orderNo: formValues.orderNo,
    };

    let updatedAssignments: DisplayContentAssignmentInput[];

    if (editingIndex !== null) {
      // Edit existing assignment
      updatedAssignments = [...currentAssignments];
      updatedAssignments[editingIndex] = newAssignment;
    } else {
      // Add new assignment
      updatedAssignments = [...currentAssignments, newAssignment];
    }

    onChange?.(updatedAssignments);
    setAssignmentModalOpened(false);
  }, [assignmentForm, currentAssignments, displayId, editingIndex, onChange]);

  // Handle removing assignment
  const handleRemoveAssignment = useCallback(
    (index: number) => {
      const updatedAssignments = currentAssignments.filter(
        (_, i) => i !== index
      );
      onChange?.(updatedAssignments);
    },
    [currentAssignments, onChange]
  );

  // Handle drag and drop reordering
  const handleDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) return;

      const items = Array.from(currentAssignments);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update order numbers
      const reorderedWithOrder = items.map((item, index) => ({
        ...item,
        orderNo: index + 1,
      }));

      onChange?.(reorderedWithOrder);
    },
    [currentAssignments, onChange]
  );

  // Filter available blocks for modal
  const filteredAvailableBlocks = useMemo(() => {
    if (!searchQuery.trim()) return availableContentBlocks;

    const query = searchQuery.toLowerCase();
    return availableContentBlocks.filter(
      (block) =>
        (block.name || "").toLowerCase().includes(query) ||
        (block.description || "").toLowerCase().includes(query) ||
        (block.contentType || "").toLowerCase().includes(query)
    );
  }, [availableContentBlocks, searchQuery]);

  // Create container styles
  const containerStyles = useMemo(
    () => ({
      minHeight: 80,
      maxHeight,
      borderRadius: theme.radius.sm,
      border: `1px solid ${error ? theme.colors.red[4] : theme.colors.gray[4]}`,
      backgroundColor:
        colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
    }),
    [theme, colorScheme, error, maxHeight]
  );

  // Show loading state in the main container
  if (loading && contentBlocks.length === 0) {
    return (
      <>
        <Box style={containerStyles}>
          <Group gap="sm" p="md" wrap="nowrap">
            <LoadingIndicator size={size} color="gray" />
            <Text size={size} c="dimmed">
              {loadingPlaceholder}
            </Text>
          </Group>
        </Box>
        {error && (
          <Text size="sm" c="red" mt="xs">
            {error}
          </Text>
        )}
      </>
    );
  }

  // Show error state in the main container
  if (lastError && !loading && contentBlocks.length === 0) {
    return (
      <>
        <Box style={containerStyles}>
          <Group gap="sm" p="md" wrap="nowrap">
            <AppIcon iconName="AlertCircle" size={16} color="red" />
            <Text size={size} c="red">
              Error loading content blocks
            </Text>
          </Group>
        </Box>
        {error && (
          <Text size="sm" c="red" mt="xs">
            {error}
          </Text>
        )}
      </>
    );
  }

  // Main component render - always show the interface
  return (
    <>
      <Box style={containerStyles}>
        {/* Current assignments area */}
        {currentAssignments.length > 0 ? (
          <ScrollArea.Autosize mah={maxHeight - 60} p="sm">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="content-block-assignments">
                {(provided: any) => (
                  <Stack
                    gap="xs"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {currentAssignments.map((assignment, index) => {
                      const contentBlock = getContentBlockById(
                        assignment.contentBlockId || ""
                      );
                      const typeInfo = getContentTypeInfo(
                        contentBlock?.contentType
                      );

                      return (
                        <Draggable
                          key={`${assignment.contentBlockId}-${index}`}
                          draggableId={`assignment-${index}`}
                          index={index}
                          isDragDisabled={disabled ?? false}
                        >
                          {(provided: any, snapshot: any) => (
                            <Paper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              p="sm"
                              withBorder
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging
                                  ? theme.colors.blue[0]
                                  : undefined,
                              }}
                            >
                              <Group
                                gap="xs"
                                wrap="nowrap"
                                style={{ alignItems: "flex-start" }}
                              >
                                <div {...provided.dragHandleProps}>
                                  <AppIcon
                                    iconName="GripVertical"
                                    size={16}
                                    style={{
                                      cursor: disabled ? "default" : "grab",
                                    }}
                                  />
                                </div>

                                <AppIcon
                                  iconName={typeInfo.icon as any}
                                  size={16}
                                />

                                <Stack gap="xs" flex="1">
                                  <Text size="sm" fw={500} truncate>
                                    {contentBlock?.name ||
                                      "Unknown Content Block"}
                                  </Text>
                                  {contentBlock?.description && (
                                    <Text size="xs" c="dimmed" truncate>
                                      {contentBlock.description}
                                    </Text>
                                  )}

                                  <Group gap="md">
                                    <Text size="xs" c="dimmed">
                                      <strong>From:</strong>{" "}
                                      {formatDateTime(assignment.startDatetime)}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      <strong>To:</strong>{" "}
                                      {formatDateTime(assignment.endDatetime)}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      <strong>Duration:</strong>{" "}
                                      {formatDuration(
                                        assignment.durationSeconds
                                      )}
                                    </Text>
                                  </Group>
                                </Stack>

                                <Group gap="xs">
                                  <Badge
                                    size="xs"
                                    color={typeInfo.color}
                                    variant="light"
                                  >
                                    {contentBlock?.contentType || "Unknown"}
                                  </Badge>

                                  <Badge size="xs" variant="outline">
                                    #{index + 1}
                                  </Badge>

                                  <Tooltip label="Edit">
                                    <ActionIcon
                                      size="sm"
                                      color="blue"
                                      variant="subtle"
                                      onClick={() => handleOpenEditModal(index)}
                                      disabled={disabled ?? false}
                                    >
                                      <AppIcon iconName="Edit" size={12} />
                                    </ActionIcon>
                                  </Tooltip>

                                  <Tooltip label="Remove">
                                    <ActionIcon
                                      size="sm"
                                      color="red"
                                      variant="subtle"
                                      onClick={() =>
                                        handleRemoveAssignment(index)
                                      }
                                      disabled={disabled ?? false}
                                    >
                                      <AppIcon iconName="X" size={12} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Group>
                            </Paper>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </ScrollArea.Autosize>
        ) : (
          <Group gap="sm" p="md" wrap="nowrap">
            {loading && contentBlocks.length > 0 && (
              <LoadingIndicator size={size} color="gray" />
            )}
            <Text size={size} c="dimmed">
              {placeholder}
            </Text>
          </Group>
        )}

        {/* Add button - always show if not disabled and we have content blocks available */}
        {!disabled && availableContentBlocks.length > 0 && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}
          >
            <Button
              size="xs"
              variant="light"
              leftSection={<AppIcon iconName="Plus" size={14} />}
              onClick={handleOpenAddModal}
            >
              Add Content Block Assignment
            </Button>
          </Group>
        )}

        {/* Show message when no content blocks are available */}
        {!loading && availableContentBlocks.length === 0 && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}
          >
            <Text size="xs" c="dimmed">
              {emptyText}
            </Text>
          </Group>
        )}
      </Box>

      {/* Error display */}
      {error && (
        <Text size="sm" c="red" mt="xs">
          {error}
        </Text>
      )}

      {/* Add/Edit assignment modal */}
      <Modal
        opened={assignmentModalOpened}
        onClose={() => setAssignmentModalOpened(false)}
        title={
          editingIndex !== null
            ? "Edit Content Block Assignment"
            : "Add Content Block Assignment"
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Select
            label="Content Block"
            placeholder="Select a content block"
            required
            data={filteredAvailableBlocks.map((block) => ({
              value: block.id || "",
              label: block.name || "Untitled",
            }))}
            searchable
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            {...assignmentForm.getInputProps("contentBlockId")}
          />

          <Grid>
            <Grid.Col span={6}>
              <DateTimePicker
                label="Start Date & Time"
                placeholder="Select start date and time"
                clearable
                {...assignmentForm.getInputProps("startDatetime")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateTimePicker
                label="End Date & Time"
                placeholder="Select end date and time"
                clearable
                {...assignmentForm.getInputProps("endDatetime")}
              />
            </Grid.Col>
          </Grid>

          <NumberInput
            label="Duration (seconds)"
            placeholder="Enter duration in seconds"
            min={1}
            allowNegative={false}
            allowDecimal={false}
            {...assignmentForm.getInputProps("durationSeconds")}
          />

          <Text size="xs" c="dimmed">
            Leave dates empty for always-active assignment. End date must be
            after start date if both are set. Duration is optional and specifies
            how long this content should be displayed.
          </Text>

          <Divider />

          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setAssignmentModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignment}
              loading={assignmentForm.values.contentBlockId ? false : undefined}
            >
              {editingIndex !== null ? "Update" : "Add"} Assignment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
